import React, { useState } from 'react';
import { Badge, Button, Col, Media, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { ApiBaseData, ApiHotel, ApiPackageModelDefinition } from '@ibe/api';
import {
  CharacteristicGroup,
  ImageCarousel,
  MapConfig,
  Price,
  ServiceSearchDetailParams,
  useConfig,
  useTheme
} from '@ibe/components';
import useCmsHotel from '../../Hooks/useCmsHotel';
import ImageGallery from 'react-image-gallery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import IncludedServices from './IncludedServices';
import CmsFlexBox from '../../Models/CMS/CmsFlexBox';
import FlexBox from '../FlexBox';
import Keys from '../../Translations/generated/hotel-details-popup.en.json.keys';
import HotelUtils from '../../Util/HotelUtils';
import CmsDescription from '../../Models/CMS/CmsDescription';
import FlexBoxUtils from '../../Util/FlexBoxUtils';
import ModalLayer from '../ModalLayer/ModalLayer';
import GalleryUtils from '../../Util/GalleryUtils';
import { useYouTubeVideoControls } from '../../Hooks/useYouTubeVideoControls';
import { useTranslation } from 'react-i18next';
import { useMount } from 'react-use';
import { calculateNights, getPricePerNight } from '../../Util/priceUtils';
import MegConfigModel from '../../Config/MegConfigModel';
import ServicesPills, { DisplayOption } from '../Hotel/ServicesPills/ServicesPills';
import CmsOffer from '../../Models/CMS/CmsOffer';
import { getHotelMapConfig } from '../../Util/mapUtils';
import IbeConfig from '../../Models/CMS/IbeConfig';
import ParkMap from '../ParkMap/ParkMap';

interface Props {
  hotelModel: ApiHotel;
  onClose: () => void;
  onSelect?: (selectedHotel: ApiHotel) => void;
  useLazyLoading?: boolean;
  packageModel?: ApiPackageModelDefinition;
  searchParams?: Partial<ServiceSearchDetailParams>;
  mapConfig?: MapConfig;
  readPriceFromRoomRates?: boolean;
  readFromCheapestRoom?: boolean;
  characteristics?: ApiBaseData[];
  offer?: CmsOffer;
  ibeConfig?: IbeConfig;
}

const HotelDetailsPopup = (props: Props) => {
  const {
    hotelModel,
    onClose,
    onSelect,
    useLazyLoading,
    packageModel,
    searchParams,
    mapConfig,
    readPriceFromRoomRates,
    readFromCheapestRoom,
    characteristics,
    offer,
    ibeConfig
  } = props;
  const PAGE_NAME = 'HOTEL_DETAILS';
  const config = useConfig() as MegConfigModel;
  const cms = useCmsHotel();
  const theme = useTheme();
  const { t } = useTranslation('hotel-details-popup');
  const priceSource =
    readFromCheapestRoom && readPriceFromRoomRates
      ? [HotelUtils.sortRoomsByPrice(hotelModel.rooms)[0]]
      : hotelModel.rooms;
  const price = readPriceFromRoomRates
    ? HotelUtils.sumPrices(
        priceSource
          .map(room => room.roomrates)
          .flat()
          .map(rr => rr.totalPrice)
      )
    : hotelModel.price;
  const hasPrice = !!price;

  const [firstDescription, setFirstDescription] = useState<string>();
  const [secondDescription, setSecondDescription] = useState<string>();
  const [thirdDescription, setThirdDescription] = useState<string>();
  const [topFlexBoxes, setTopFlexBoxes] = useState<CmsFlexBox[]>();
  const [initialGalleryImageIndex, setInitialGalleryImageIndex] = useState<number | undefined>(
    undefined
  );
  const { handleYoutubeReady, stopVideos } = useYouTubeVideoControls();

  const nights =
    !!searchParams?.startDate && !!searchParams?.endDate
      ? calculateNights(searchParams?.startDate, searchParams?.endDate)
      : 0;
  const displayPerNightPrice = !!config.enablePerNightPrice && nights > 1;

  useMount(async () => {
    await fetchCmsHotelDetails();
  });

  const onHotelSelect = () => {
    if (onSelect) {
      onSelect(hotelModel);
    }
    onClose();
  };

  const setDescriptionStates = (descriptions: CmsDescription[]) => {
    const descriptionStates = [setFirstDescription, setSecondDescription, setThirdDescription];

    descriptions.forEach((description, index) => {
      descriptionStates[index](description?.content || undefined);
    });
  };

  const fetchCmsHotelDetails = (): Promise<void> => {
    return cms.fetchHotel(hotelModel.code).then(cmsHotel => {
      setDescriptionStates(cmsHotel?.descriptions || []);
      if (cmsHotel?.flexBoxes) {
        setTopFlexBoxes(FlexBoxUtils.getFlexBoxes(cmsHotel.flexBoxes, 'TOP', PAGE_NAME));
      }
    });
  };

  const hasSpecialAmenities = (): boolean => {
    return !!hotelModel.specialAmenities && hotelModel.specialAmenities.length > 0;
  };

  const AvailabilityInfoBox = () =>
    hotelModel.availability && hotelModel.availability <= config.offersLeftMaxToDisplay ? (
      <div className="hotel-overlay__availability badge-secondary d-flex justify-content-end mt-2 py-1 px-2">
        {hotelModel.availability + ' ' + t(Keys.offerLeft, { count: hotelModel.availability })}
      </div>
    ) : (
      <></>
    );

  const getPriceWithNightLabel = () => {
    if (searchParams?.startDate && searchParams?.endDate) {
      const startDate = new Date(searchParams?.startDate);
      const endDate = new Date(searchParams?.endDate);
      const diff = Math.abs(startDate.getTime() - endDate.getTime());
      const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
      if (diffDays > 0) {
        return `${diffDays} ${t(Keys.priceLabelWithNight, { count: diffDays })}`;
      }
    }
  };

  const Gallery = () => (
    <div className={'hotel-overlay__gallery'}>
      <AvailabilityInfoBox />
      <ImageCarousel
        images={GalleryUtils.getImagesForCarousel(hotelModel.media)}
        defaultImage={(theme.placeholderImages && theme.placeholderImages.hotel) || ''}
        onImageClick={(imageIndex: number): void => {
          setInitialGalleryImageIndex(imageIndex);
        }}
      />
      <ModalLayer
        isOpen={initialGalleryImageIndex !== undefined}
        toggle={() => setInitialGalleryImageIndex(undefined)}
      >
        <ImageGallery
          items={GalleryUtils.getImageGalleryItems(hotelModel.media, handleYoutubeReady)}
          showFullscreenButton={false}
          showPlayButton={false}
          startIndex={initialGalleryImageIndex}
          thumbnailPosition="bottom"
          lazyLoad={useLazyLoading}
          onSlide={stopVideos}
        />
      </ModalLayer>
    </div>
  );

  const Amenities = () => {
    return hasSpecialAmenities() ? (
      <Row className="mb-3">
        <Col className="d-flex flex-wrap justify-content-start">
          {hotelModel.specialAmenities?.map((specialAmenity, index) => (
            <div
              className={'hotel-overlay__amenity-image'}
              key={specialAmenity.description + index}
            >
              <Media
                src={specialAmenity?.image?.small?.url}
                alt={specialAmenity?.image?.imageAlt}
              />
            </div>
          ))}
        </Col>
      </Row>
    ) : (
      <></>
    );
  };

  const Characteristics = () => {
    const tagsCharacteristics = hotelModel.characteristics;
    const tagsCharacteristicsFiltered = tagsCharacteristics?.filter(characteristic => {
      return (
        !characteristic.group ||
        (characteristic.group &&
          !Object.values(CharacteristicGroup).includes(characteristic.group as CharacteristicGroup))
      );
    });

    return !!tagsCharacteristicsFiltered ? (
      <Row className={'mb-3'}>
        <Col>
          <div className={`TagsLayout__container`}>
            <div className="TagsLayout__inner">
              {tagsCharacteristicsFiltered.map(ele => {
                return (
                  <span key={ele.description.toString()} className="TagsLayout">
                    <Badge color={'secondary'} pill>
                      {ele.description}
                    </Badge>
                  </span>
                );
              })}
            </div>
          </div>
        </Col>
      </Row>
    ) : (
      <></>
    );
  };

  return (
    <Modal
      isOpen={true}
      className={'hotel-overlay'}
      data-overlay-type={'hotel-details'}
      data-hotel-code={hotelModel.code}
      container={document.getElementById('iso') || undefined}
      toggle={onClose}
    >
      <ModalBody className="hotel-overlay__content">
        <ModalHeader
          className="hotel-overlay__header"
          close={
            <button
              className="hotel-overlay__close"
              onClick={onClose}
              type="button"
              aria-label={t(Keys.close)}
              data-action={'close-overlay'}
            >
              <span aria-hidden="true">×</span>
            </button>
          }
        ></ModalHeader>
        <Row>
          <Col className="hotel-overlay__package-name">
            <h5>{packageModel?.name}</h5>
          </Col>
        </Row>
        <Row>
          <Col className="hotel-overlay__hotel-name">
            <h4>{hotelModel.name}</h4>
          </Col>
        </Row>
        {hotelModel.distance && hotelModel.distance.description && (
          <Row>
            <Col className="hotel-overlay__location">
              <FontAwesomeIcon className="fa-light" icon={faMapMarkerAlt} />{' '}
              {hotelModel.distance.description}
            </Col>
          </Row>
        )}
        <Row className={'my-3'}>
          <Col className="hotel-overlay__gallery-container">
            <Gallery />
          </Col>
        </Row>
        <Row>
          {hasPrice ? (
            <Col className={'col-12 col-md-6 hotel-overlay__price-container'}>
              <p className="hotel-overlay__price__night-label">{getPriceWithNightLabel()}</p>
              <Price
                price={displayPerNightPrice ? getPricePerNight(price, nights) : price}
                align={'left'}
                roundPrice={config.roundPricesToWholeNumber}
                decimalPosition={'TOP'}
                className="hotel-overlay__price"
              />
              {displayPerNightPrice && (
                <Row className={'mb-0'}>
                  <Col className={'iso__price-list-item-layout__totalPrice'}>
                    <Price
                      price={price}
                      align="left"
                      roundPrice={config.roundPricesToWholeNumber}
                      decimalPosition={'TOP'}
                      prefix={`${t(Keys.perNight)}. ${t(Keys.total)}: `}
                    />
                  </Col>
                </Row>
              )}
            </Col>
          ) : (
            <Col className={'col-12 col-md-6 order-last order-md-first d-flex align-items-center'}>
              {characteristics && characteristics.length > 0 && (
                <Row className={'justify-content-between'}>
                  <Col xs={12}>
                    <ServicesPills items={characteristics} displayOption={DisplayOption.PACKAGE} />
                  </Col>
                </Row>
              )}
            </Col>
          )}
          <Col
            className={
              'd-flex justify-content-end p-3 col-12 col-md-6 hotel-overlay__button-container'
            }
          >
            {mapConfig && (
              <ParkMap
                mapConfig={getHotelMapConfig(hotelModel, ibeConfig)}
                displayMode={'BUTTON'}
                mapButtonLabel={t(Keys.mapButtonLabel)}
              />
            )}
            {onSelect && (
              <Button
                color="primary"
                className={'hotel-overlay__select-button w-100 ml-3'}
                hotel-model={'select-hotel'}
                onClick={onHotelSelect}
              >
                {t(Keys.selectButtonLabel)}
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            )}
          </Col>
        </Row>
        {!hasPrice && ((characteristics && characteristics.length > 0) || offer) && (
          <Row className={'justify-content-between'}>
            <Col xs={12}>
              <ServicesPills
                items={characteristics || []}
                displayOption={DisplayOption.HOTEL}
                offer={offer}
              />
            </Col>
          </Row>
        )}
        {hasPrice && ((characteristics && characteristics.length > 0) || offer) && (
          <>
            <Row className={'justify-content-between'}>
              <Col xs={12}>
                <ServicesPills
                  items={characteristics || []}
                  displayOption={DisplayOption.PACKAGE}
                />
              </Col>
            </Row>
            <Row className={'justify-content-between'}>
              <Col xs={12}>
                <ServicesPills
                  items={characteristics || []}
                  displayOption={DisplayOption.HOTEL}
                  offer={offer}
                />
              </Col>
            </Row>
          </>
        )}
        <Row className={`d-none ${hasPrice && 'd-md-block'}`}>
          <Col className={'col-12 col-md-6 d-flex justify-content-start align-items-center'}>
            <IncludedServices
              hotelModel={hotelModel}
              packageIncludedServices={packageModel?.includedServices}
              searchParams={searchParams}
            />
          </Col>
        </Row>
        <Row className={'mb-3 hotel-overlay__first-description'}>
          <Col
            dangerouslySetInnerHTML={{
              __html: firstDescription || ''
            }}
          ></Col>
        </Row>
        <Amenities />
        <Characteristics />
        {!!topFlexBoxes && (
          <Row className={'mb-3'}>
            <Col>
              <FlexBox flexBoxes={topFlexBoxes} className={'hotel-overlay__flexbox'} />
            </Col>
          </Row>
        )}
        <Row className={'mb-3 hotel-overlay__second-description'}>
          <Col
            dangerouslySetInnerHTML={{
              __html: secondDescription || ''
            }}
          ></Col>
        </Row>
        <Row className={'mb-3 hotel-overlay__third-description'}>
          <Col
            dangerouslySetInnerHTML={{
              __html: thirdDescription || ''
            }}
          ></Col>
        </Row>
        {onSelect && (
          <Row>
            <Col>
              <Button
                color="primary"
                className={'hotel-overlay__select-button w-100'}
                hotel-model={'select-hotel'}
                onClick={onHotelSelect}
              >
                {t(Keys.selectButtonLabel)}
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </Col>
          </Row>
        )}
      </ModalBody>
    </Modal>
  );
};

export default HotelDetailsPopup;
